import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { CreateCustomGroupModal, type CreateCustomGroupSaveData } from '@/components/modals/CreateCustomGroupModal';
import { PermissionsGroupsTab } from '@/components/screens/permissions/PermissionsGroupsTab';
import { PermissionsUsersTab } from '@/components/screens/permissions/PermissionsUsersTab';
import { PermissionsAuditLog } from '@/components/screens/permissions/PermissionsAuditLog';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// ── Global Declarations ──────────────────────────────────────────────

import { getAvailableSubmodules } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
const MAX_CUSTOM_GROUPS_PER_COMPANY = (window as any).MAX_CUSTOM_GROUPS_PER_COMPANY || 20;
const MAX_GROUP_TITLE_LENGTH = (window as any).MAX_GROUP_TITLE_LENGTH || 50;

// ── Types ────────────────────────────────────────────────────────────

interface PermissionGroup {
  id: number | null;
  title: string;
  type?: string;
  permissions: string[];
  requiredRoles: string[];
  allowedRoles: string[];
}

interface PermissionRow {
  id: number;
  name: string;
  permission: string;
  parentId: number | null;
}

interface PermissionCategory {
  id: number;
  name: string;
  permission: string;
  children: PermissionRow[];
}

interface PermissionPerson {
  id: number;
  name: string;
  hidden?: boolean;
}

interface PersonGroupAssignment {
  person: number;
  permissionGroup: number | null;
}

interface ChildRow {
  id: number;
  name: string;
  permission: string;
  parentId: number | null;
  indent: number;
}

// ── Helper Functions ─────────────────────────────────────────────────

function getGroupKey(groupId: number | null): string {
  return groupId === null ? 'new' : String(groupId);
}

function isCustomGroup(group: PermissionGroup): boolean {
  return group.requiredRoles.length === 0;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function checkAuditLogPermission(): boolean {
  let submodules: string[] = getAvailableSubmodules();
  if (submodules === null) {
    submodules = [];
  }
  return isInArray(submodules, AccessAuthorities.ROLE_PERMISSIONS_AUDIT_LOG);
}

function computeChildRows(category: PermissionCategory): ChildRow[] {
  const result: ChildRow[] = [];

  if (category.permission) {
    result.push({
      id: category.id,
      name: category.name + ' Access',
      permission: category.permission,
      parentId: null,
      indent: 1,
    });
  }

  for (let i = 0; i < category.children.length; i++) {
    const child = category.children[i];
    if (child.parentId === category.id) {
      result.push({
        id: child.id,
        name: child.name,
        permission: child.permission,
        parentId: child.parentId,
        indent: 1,
      });

      for (let j = 0; j < category.children.length; j++) {
        const grandchild = category.children[j];
        if (grandchild.parentId === child.id) {
          result.push({
            id: grandchild.id,
            name: grandchild.name,
            permission: grandchild.permission,
            parentId: grandchild.parentId,
            indent: 2,
          });
        }
      }
    }
  }

  return result;
}

function buildCategories(permissionRows: PermissionRow[]): PermissionCategory[] {
  const categoryMap: { [key: number]: PermissionCategory } = {};

  for (let i = 0; i < permissionRows.length; i++) {
    const row = permissionRows[i];
    if (row.parentId === null) {
      categoryMap[row.id] = {
        id: row.id,
        name: row.name,
        permission: row.permission,
        children: [],
      };
    }
  }

  for (let i = 0; i < permissionRows.length; i++) {
    const row = permissionRows[i];
    if (row.parentId !== null) {
      let parentId = row.parentId;
      let parentRow: PermissionRow | null = null;

      for (let j = 0; j < permissionRows.length; j++) {
        if (permissionRows[j].id === parentId) {
          parentRow = permissionRows[j];
          break;
        }
      }

      while (parentRow && parentRow.parentId !== null) {
        parentId = parentRow.parentId;
        parentRow = null;
        for (let j = 0; j < permissionRows.length; j++) {
          if (permissionRows[j].id === parentId) {
            parentRow = permissionRows[j];
            break;
          }
        }
      }

      const category = categoryMap[parentId];
      if (category) {
        category.children.push(row);
      }
    }
  }

  const categories: PermissionCategory[] = [];
  for (const key in categoryMap) {
    if (categoryMap.hasOwnProperty(key)) {
      categories.push(categoryMap[key]);
    }
  }
  return categories;
}

function buildChildRowsCache(categories: PermissionCategory[]): { [categoryId: number]: ChildRow[] } {
  const cache: { [categoryId: number]: ChildRow[] } = {};
  for (let c = 0; c < categories.length; c++) {
    cache[categories[c].id] = computeChildRows(categories[c]);
  }
  return cache;
}

function getAllPermissionsInCategory(category: PermissionCategory): string[] {
  const perms: string[] = [];
  if (category.permission) {
    perms.push(category.permission);
  }
  for (let i = 0; i < category.children.length; i++) {
    if (category.children[i].permission) {
      perms.push(category.children[i].permission);
    }
  }
  return perms;
}

function isPermissionValidForGroup(permission: string, group: PermissionGroup): boolean {
  return group.requiredRoles.indexOf(permission) > -1 ||
         group.allowedRoles.indexOf(permission) > -1;
}

function buildAllowedPermissionsSet(
  permissionGroups: PermissionGroup[]
): { [perm: string]: boolean } {
  const allowedPermissionsSet: { [perm: string]: boolean } = {};
  for (let g = 0; g < permissionGroups.length; g++) {
    const group = permissionGroups[g];
    for (let a = 0; a < group.allowedRoles.length; a++) {
      allowedPermissionsSet[group.allowedRoles[a]] = true;
    }
  }
  return allowedPermissionsSet;
}

// ── Component ────────────────────────────────────────────────────────

export function PermissionsScreen() {
  const { theme } = useTheme();

  // ── Data state ──
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [allPermissionGroups, setAllPermissionGroups] = useState<PermissionGroup[]>([]);
  const [categories, setCategories] = useState<PermissionCategory[]>([]);
  const [people, setPeople] = useState<PermissionPerson[]>([]);
  const [permissionGroupList, setPermissionGroupList] = useState<PersonGroupAssignment[]>([]);

  // ── UI state ──
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'groups' | 'users' | 'auditlog'>('groups');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleGroupIds, setVisibleGroupIds] = useState<(number | null)[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ── Permission store ──
  const [permissionStore, setPermissionStore] = useState<{ [groupId: string]: { [permission: string]: boolean } }>({});

  // ── Initial state caches for discard ──
  const initialPermissionStoreRef = useRef<{ [groupId: string]: { [permission: string]: boolean } }>({});
  const initialPermissionGroupListRef = useRef<PersonGroupAssignment[]>([]);
  const initialPermissionGroupsRef = useRef<PermissionGroup[]>([]);
  const initialVisibleGroupIdsRef = useRef<(number | null)[]>([]);
  const initialCustomGroupTitlesRef = useRef<{ [groupId: string]: string }>({});

  // ── Custom group management ──
  const nextTempIdRef = useRef(-1);
  const [deletedCustomGroupIds, setDeletedCustomGroupIds] = useState<number[]>([]);
  const [customGroupTitles, setCustomGroupTitles] = useState<{ [groupId: string]: string }>({});
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const preReloadGroupIdsRef = useRef<(number | null)[]>([]);

  // ── Modal state ──
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalGroupId, setDeleteModalGroupId] = useState<number | null>(null);

  // ── Audit log ──
  const [hasAuditLogPermission] = useState(() => checkAuditLogPermission());

  // ── Refs ──
  const groupTitleInputRef = useRef<HTMLInputElement>(null);
  const childRowsCacheRef = useRef<{ [categoryId: number]: ChildRow[] }>({});

  // ── Computed values ──

  const visibleGroups = useMemo(
    () => permissionGroups.filter((g) => visibleGroupIds.indexOf(g.id) > -1),
    [permissionGroups, visibleGroupIds]
  );

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    const term = searchTerm.toLowerCase();
    return categories.filter((cat) => {
      if (cat.name.toLowerCase().indexOf(term) > -1) return true;
      for (let i = 0; i < cat.children.length; i++) {
        if (cat.children[i].name.toLowerCase().indexOf(term) > -1) return true;
      }
      return false;
    });
  }, [categories, searchTerm]);

  const sortedPeople = useMemo(
    () =>
      people
        .filter((p) => !p.hidden)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [people]
  );

  const canCreateCustomGroup = useMemo(() => {
    let count = 0;
    for (let i = 0; i < permissionGroups.length; i++) {
      if (isCustomGroup(permissionGroups[i])) {
        count++;
      }
    }
    return count < MAX_CUSTOM_GROUPS_PER_COMPANY;
  }, [permissionGroups]);

  const personGroupLookup = useMemo(() => {
    const lookup: { [key: string]: boolean } = {};
    for (let i = 0; i < permissionGroupList.length; i++) {
      const pgl = permissionGroupList[i];
      lookup[pgl.person + '_' + pgl.permissionGroup] = true;
    }
    return lookup;
  }, [permissionGroupList]);

  const selectedPeopleLookup = useMemo(() => {
    const lookup: { [id: number]: boolean } = {};
    for (let i = 0; i < selectedPeople.length; i++) {
      lookup[selectedPeople[i]] = true;
    }
    return lookup;
  }, [selectedPeople]);

  const deleteModalMessageHtml = useMemo(() => {
    let groupTitle = '';
    let memberCount = 0;
    if (deleteModalGroupId !== null) {
      const group = permissionGroups.find((g) => g.id === deleteModalGroupId);
      if (group) {
        const gKey = getGroupKey(group.id);
        groupTitle = customGroupTitles[gKey] || group.title;
      }
      for (let i = 0; i < permissionGroupList.length; i++) {
        if (permissionGroupList[i].permissionGroup === deleteModalGroupId) {
          memberCount++;
        }
      }
    }
    let html = 'Are you sure you want to delete <strong>' + escapeHtml(groupTitle) + '</strong>?';
    if (memberCount > 0) {
      html += '<br><br><span style="color: #dc3545;"><i class="fa fa-exclamation-triangle"></i> ' + memberCount + ' user(s) will lose their permissions from this group.</span>';
    }
    html += '<br><br><em>This action will take effect when you save changes.</em>';
    return html;
  }, [deleteModalGroupId, permissionGroups, permissionGroupList, customGroupTitles]);

  const categoriesForModal = useMemo(() => {
    const allowedPermissionsSet = buildAllowedPermissionsSet(permissionGroups);
    return categories.map((category) => {
      const children = childRowsCacheRef.current[category.id] || [];
      const selectableChildren = children.filter(
        (child) => child.permission && child.permission.length > 0 && allowedPermissionsSet[child.permission]
      );
      return {
        id: category.id,
        name: category.name,
        children: selectableChildren.map((child) => ({
          id: child.id,
          name: child.name,
          permission: child.permission,
        })),
      };
    });
  }, [categories, permissionGroups]);

  const existingGroupTitles = useMemo(
    () =>
      permissionGroups.map((group) => {
        const gKey = getGroupKey(group.id);
        return customGroupTitles[gKey] || group.title;
      }),
    [permissionGroups, customGroupTitles]
  );

  // ── Helpers ──

  const isGroupVisible = useCallback(
    (groupId: number | null) => visibleGroupIds.indexOf(groupId) > -1,
    [visibleGroupIds]
  );

  const isPersonSelected = useCallback(
    (personId: number) => selectedPeopleLookup[personId] === true,
    [selectedPeopleLookup]
  );

  const isPersonInGroup = useCallback(
    (personId: number, groupId: number | null) => personGroupLookup[personId + '_' + groupId] === true,
    [personGroupLookup]
  );

  const getGroupDisplayTitle = useCallback(
    (group: PermissionGroup) => {
      const gKey = getGroupKey(group.id);
      if (isCustomGroup(group) && customGroupTitles[gKey] !== undefined) {
        return customGroupTitles[gKey];
      }
      return group.title;
    },
    [customGroupTitles]
  );

  const getCategoryChildRows = useCallback(
    (categoryId: number): ChildRow[] => childRowsCacheRef.current[categoryId] || [],
    []
  );

  const isCategoryExpanded = useCallback(
    (categoryId: number): boolean => {
      if (searchTerm) {
        const category = categories.find((c) => c.id === categoryId);
        if (category) {
          const term = searchTerm.toLowerCase();
          for (let i = 0; i < category.children.length; i++) {
            if (category.children[i].name.toLowerCase().indexOf(term) > -1) {
              return true;
            }
          }
        }
      }
      return expandedCategories.indexOf(categoryId) > -1;
    },
    [searchTerm, categories, expandedCategories]
  );

  const isSearchMatch = useCallback(
    (name: string): boolean => {
      return !!searchTerm && name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
    },
    [searchTerm]
  );

  const getAllAvailablePermissions = useCallback((): string[] => {
    const allowedPermissionsSet = buildAllowedPermissionsSet(permissionGroups);
    const perms: string[] = [];
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      if (category.permission && allowedPermissionsSet[category.permission]) {
        perms.push(category.permission);
      }
      for (let j = 0; j < category.children.length; j++) {
        const childPerm = category.children[j].permission;
        if (childPerm && allowedPermissionsSet[childPerm]) {
          perms.push(childPerm);
        }
      }
    }
    return perms;
  }, [permissionGroups, categories]);

  // ── localStorage persistence ──

  const saveExpandedState = useCallback((expanded: number[]) => {
    localStorage.setItem('permissionsExpandedCategories', JSON.stringify(expanded));
  }, []);

  const saveVisibleGroups = useCallback((ids: (number | null)[]) => {
    localStorage.setItem('permissionsVisibleGroups', JSON.stringify(ids));
  }, []);

  // ── Data loading ──

  const loadPermissions = useCallback(() => {
    api.getPermissionList((result: any) => {
      const groups: PermissionGroup[] = result.permissionGroups || [];
      const rows: PermissionRow[] = result.rows || [];
      const ppl: PermissionPerson[] = (result.people || []).sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      );
      const pgl: PersonGroupAssignment[] = result.permissionGroupList || [];

      const cats = buildCategories(rows);
      const cache = buildChildRowsCache(cats);
      childRowsCacheRef.current = cache;

      // Build permission store
      const newStore: { [groupId: string]: { [permission: string]: boolean } } = {};
      const newInitialStore: { [groupId: string]: { [permission: string]: boolean } } = {};
      const newTitles: { [groupId: string]: string } = {};
      const newInitialTitles: { [groupId: string]: string } = {};

      for (let g = 0; g < groups.length; g++) {
        const group = groups[g];
        const gKey = getGroupKey(group.id);
        const perms: { [permission: string]: boolean } = {};
        const initialPerms: { [permission: string]: boolean } = {};
        for (let p = 0; p < group.permissions.length; p++) {
          perms[group.permissions[p]] = true;
          initialPerms[group.permissions[p]] = true;
        }
        newStore[gKey] = perms;
        newInitialStore[gKey] = initialPerms;
        if (isCustomGroup(group)) {
          newTitles[gKey] = group.title;
          newInitialTitles[gKey] = group.title;
        }
      }

      // Initialize visible groups
      let visibleIds: (number | null)[];
      const saved = localStorage.getItem('permissionsVisibleGroups');
      if (saved) {
        try {
          const savedIds = JSON.parse(saved);
          visibleIds = savedIds.filter((id: number) => {
            for (let i = 0; i < groups.length; i++) {
              if (groups[i].id === id) return true;
            }
            return false;
          });
          if (visibleIds.length === 0) {
            visibleIds = groups.map((g) => g.id);
          }
        } catch (e) {
          visibleIds = groups.map((g) => g.id);
        }
      } else {
        visibleIds = groups.map((g) => g.id);
      }

      // Add new groups to visible (after save/reload)
      const preReloadIds = preReloadGroupIdsRef.current;
      if (preReloadIds.length > 0) {
        let hasNewGroups = false;
        for (let i = 0; i < groups.length; i++) {
          const group = groups[i];
          if (group.id !== null && (group.id as number) > 0) {
            let isNew = true;
            for (let j = 0; j < preReloadIds.length; j++) {
              if (preReloadIds[j] === group.id) {
                isNew = false;
                break;
              }
            }
            if (isNew && visibleIds.indexOf(group.id) === -1) {
              visibleIds.push(group.id);
              hasNewGroups = true;
            }
          }
        }
        preReloadGroupIdsRef.current = [];
        if (hasNewGroups) {
          localStorage.setItem('permissionsVisibleGroups', JSON.stringify(visibleIds));
        }
      }

      // Load expanded state
      let expanded: number[] = [];
      const savedExpanded = localStorage.getItem('permissionsExpandedCategories');
      if (savedExpanded) {
        try {
          expanded = JSON.parse(savedExpanded);
        } catch (e) {
          expanded = [];
        }
      }

      // Cache initial state
      initialPermissionStoreRef.current = newInitialStore;
      initialPermissionGroupListRef.current = JSON.parse(JSON.stringify(pgl));
      initialPermissionGroupsRef.current = JSON.parse(JSON.stringify(groups));
      initialVisibleGroupIdsRef.current = visibleIds.slice();
      initialCustomGroupTitlesRef.current = { ...newInitialTitles };

      // Set all state
      setPermissionGroups(groups);
      setCategories(cats);
      setPeople(ppl);
      setPermissionGroupList(pgl);
      setPermissionStore(newStore);
      setCustomGroupTitles(newTitles);
      setVisibleGroupIds(visibleIds);
      setExpandedCategories(expanded);
      setDeletedCustomGroupIds([]);
      nextTempIdRef.current = -1;
      setIsLoading(false);
    });

    api.getPermissionGroups((result: any) => {
      setAllPermissionGroups(result || []);
    });
  }, []);

  // ── Mount ──

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // ── Audit log tab ──
  // Rendered directly as <PermissionsAuditLog /> in JSX below.

  // ── Focus editing input when editingGroupId changes ──

  useEffect(() => {
    if (editingGroupId !== null && groupTitleInputRef.current) {
      groupTitleInputRef.current.focus();
      groupTitleInputRef.current.select();
    }
  }, [editingGroupId]);

  // ── Actions ──

  const switchTab = useCallback((tab: 'groups' | 'users' | 'auditlog') => {
    setActiveTab(tab);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const expandAll = useCallback(() => {
    const allIds = categories.map((c) => c.id);
    setExpandedCategories(allIds);
    saveExpandedState(allIds);
  }, [categories, saveExpandedState]);

  const collapseAll = useCallback(() => {
    setExpandedCategories([]);
    saveExpandedState([]);
  }, [saveExpandedState]);

  const toggleCategory = useCallback(
    (categoryId: number, event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest && target.closest('.category-cell')) return;

      setExpandedCategories((prev) => {
        const index = prev.indexOf(categoryId);
        let next: number[];
        if (index === -1) {
          next = [...prev, categoryId];
        } else {
          next = prev.filter((id) => id !== categoryId);
        }
        saveExpandedState(next);
        return next;
      });
    },
    [saveExpandedState]
  );

  const toggleGroupVisibility = useCallback(
    (groupId: number | null) => {
      setVisibleGroupIds((prev) => {
        const index = prev.indexOf(groupId);
        let next: (number | null)[];
        if (index === -1) {
          next = [...prev, groupId];
        } else {
          next = prev.filter((id) => id !== groupId);
        }
        saveVisibleGroups(next);
        return next;
      });
    },
    [saveVisibleGroups]
  );

  const showAllGroups = useCallback(() => {
    const allIds = permissionGroups.map((g) => g.id);
    setVisibleGroupIds(allIds);
    saveVisibleGroups(allIds);
  }, [permissionGroups, saveVisibleGroups]);

  const hideAllGroups = useCallback(() => {
    setVisibleGroupIds([]);
    saveVisibleGroups([]);
  }, [saveVisibleGroups]);

  const togglePermission = useCallback(
    (groupId: number | null, permission: string, shouldEnable: boolean) => {
      const gKey = getGroupKey(groupId);
      setPermissionStore((prev) => {
        const groupPerms = { ...(prev[gKey] || {}) };
        if (shouldEnable) {
          groupPerms[permission] = true;
        } else {
          delete groupPerms[permission];
        }
        return { ...prev, [gKey]: groupPerms };
      });
    },
    []
  );

  const toggleCategoryPermissions = useCallback(
    (categoryId: number, groupId: number | null) => {
      const category = categories.find((c) => c.id === categoryId);
      const group = permissionGroups.find((g) => g.id === groupId);
      if (!category || !group) return;

      const allPerms = getAllPermissionsInCategory(category);
      const gKey = getGroupKey(groupId);

      setPermissionStore((prev) => {
        const groupPerms = prev[gKey] || {};
        const allowedPerms: string[] = [];
        let allowedEnabledCount = 0;

        for (let i = 0; i < allPerms.length; i++) {
          const perm = allPerms[i];
          if (group.allowedRoles.indexOf(perm) > -1) {
            allowedPerms.push(perm);
            if (groupPerms[perm]) {
              allowedEnabledCount++;
            }
          }
        }

        const shouldEnable = allowedEnabledCount < allowedPerms.length;
        const newPerms = { ...groupPerms };

        for (let j = 0; j < allowedPerms.length; j++) {
          if (shouldEnable) {
            newPerms[allowedPerms[j]] = true;
          } else {
            delete newPerms[allowedPerms[j]];
          }
        }

        return { ...prev, [gKey]: newPerms };
      });
    },
    [categories, permissionGroups]
  );

  const togglePersonSelection = useCallback((personId: number) => {
    setSelectedPeople((prev) => {
      const index = prev.indexOf(personId);
      if (index === -1) {
        return [...prev, personId];
      }
      return prev.filter((id) => id !== personId);
    });
  }, []);

  const addSelectedToGroup = useCallback(
    (groupId: number | null) => {
      setPermissionGroupList((prev) => {
        const newList = [...prev];
        for (let i = 0; i < selectedPeople.length; i++) {
          const personId = selectedPeople[i];
          const alreadyIn = newList.some(
            (pgl) => pgl.person === personId && pgl.permissionGroup === groupId
          );
          if (!alreadyIn) {
            newList.push({ person: personId, permissionGroup: groupId });
          }
        }
        return newList;
      });
    },
    [selectedPeople]
  );

  const removeSelectedFromGroup = useCallback(
    (groupId: number | null) => {
      setPermissionGroupList((prev) => {
        let newList = [...prev];
        for (let i = 0; i < selectedPeople.length; i++) {
          const personId = selectedPeople[i];
          for (let j = newList.length - 1; j >= 0; j--) {
            if (newList[j].person === personId && newList[j].permissionGroup === groupId) {
              newList = [...newList.slice(0, j), ...newList.slice(j + 1)];
              break;
            }
          }
        }
        return newList;
      });
    },
    [selectedPeople]
  );

  // ── Custom group management ──

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleCreateModalSave = useCallback(
    (data: CreateCustomGroupSaveData) => {
      const title = data.title;
      const permissions = data.permissions;
      const newPeople = data.people;

      const tempId = nextTempIdRef.current--;
      const newGroup: PermissionGroup = {
        id: tempId,
        title: title,
        type: 'CUSTOM',
        permissions: permissions,
        requiredRoles: [],
        allowedRoles: getAllAvailablePermissions(),
      };

      setPermissionGroups((prev) => [...prev, newGroup]);

      const gKey = getGroupKey(tempId);
      const newPerms: { [permission: string]: boolean } = {};
      for (let i = 0; i < permissions.length; i++) {
        newPerms[permissions[i]] = true;
      }
      setPermissionStore((prev) => ({ ...prev, [gKey]: newPerms }));

      setCustomGroupTitles((prev) => ({ ...prev, [gKey]: title }));

      setPermissionGroupList((prev) => {
        const additions = newPeople.map((personId) => ({
          person: personId,
          permissionGroup: tempId as number | null,
        }));
        return [...prev, ...additions];
      });

      setVisibleGroupIds((prev) => {
        const next = [...prev, tempId as number | null];
        saveVisibleGroups(next);
        return next;
      });

      // Rebuild child rows cache (categories data may be needed for the new group)
      childRowsCacheRef.current = buildChildRowsCache(categories);

      setShowCreateModal(false);
      App.showPopupNotification('Custom group created. Save to apply changes.', 'success');
    },
    [getAllAvailablePermissions, categories, saveVisibleGroups]
  );

  const startEditingGroupTitle = useCallback((groupId: number) => {
    setEditingGroupId(groupId);
  }, []);

  const saveGroupTitle = useCallback(
    (groupId: number, event: React.KeyboardEvent | React.MouseEvent) => {
      let input: HTMLInputElement | null = null;
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT') {
        input = target as HTMLInputElement;
      } else {
        input = groupTitleInputRef.current;
      }
      if (!input) return;

      const newTitle = input.value.trim();
      if (!newTitle) {
        App.showPopupNotification('Group name cannot be empty.', 'error');
        return;
      }

      if (newTitle.length > MAX_GROUP_TITLE_LENGTH) {
        App.showPopupNotification('Group name must be ' + MAX_GROUP_TITLE_LENGTH + ' characters or fewer.', 'error');
        return;
      }

      // Check for duplicate
      for (let i = 0; i < permissionGroups.length; i++) {
        const group = permissionGroups[i];
        if (group.id !== groupId) {
          const gKey = getGroupKey(group.id);
          const existingTitle = customGroupTitles[gKey] || group.title;
          if (existingTitle.toLowerCase() === newTitle.toLowerCase()) {
            App.showPopupNotification('A group with this name already exists.', 'error');
            return;
          }
        }
      }

      const gKey = getGroupKey(groupId);
      setCustomGroupTitles((prev) => ({ ...prev, [gKey]: newTitle }));
      setEditingGroupId(null);
    },
    [permissionGroups, customGroupTitles]
  );

  const cancelEditingGroupTitle = useCallback(() => {
    setEditingGroupId(null);
  }, []);

  const openDeleteModal = useCallback((groupId: number) => {
    setShowDeleteModal(true);
    setDeleteModalGroupId(groupId);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteModalGroupId(null);
  }, []);

  const confirmDeleteGroup = useCallback(() => {
    if (deleteModalGroupId === null) return;

    const groupId = deleteModalGroupId;

    setPermissionGroups((prev) => prev.filter((g) => g.id !== groupId));

    setPermissionGroupList((prev) => prev.filter((pgl) => pgl.permissionGroup !== groupId));

    setVisibleGroupIds((prev) => prev.filter((id) => id !== groupId));

    if (groupId > 0) {
      setDeletedCustomGroupIds((prev) => [...prev, groupId]);
    }

    const gKey = getGroupKey(groupId);
    setPermissionStore((prev) => {
      const { [gKey]: _, ...rest } = prev;
      return rest;
    });
    setCustomGroupTitles((prev) => {
      const { [gKey]: _, ...rest } = prev;
      return rest;
    });

    setShowDeleteModal(false);
    setDeleteModalGroupId(null);
    App.showPopupNotification('Custom group deleted. Save to apply changes.', 'warning');
  }, [deleteModalGroupId]);

  // ── Save / Discard ──

  const savePermissions = useCallback(() => {
    setIsSaving(true);

    const newPermList: any[] = [];

    for (let i = 0; i < permissionGroups.length; i++) {
      const group = permissionGroups[i];

      if (group.id !== null && (group.id as number) > 0 && deletedCustomGroupIds.indexOf(group.id as number) > -1) {
        continue;
      }

      const gKey = getGroupKey(group.id);
      const permissions: string[] = [];
      const filteredOut: string[] = [];
      const groupPerms = permissionStore[gKey] || {};
      for (const perm in groupPerms) {
        if (groupPerms.hasOwnProperty(perm) && groupPerms[perm]) {
          if (isPermissionValidForGroup(perm, group)) {
            permissions.push(perm);
          } else {
            filteredOut.push(perm);
          }
        }
      }

      const personGrpList = permissionGroupList.filter((pgl) => pgl.permissionGroup === group.id);

      const groupData: any = {
        id: group.id !== null && (group.id as number) < 0 ? null : group.id,
        permissions: permissions,
        people: personGrpList.map((pgl) => pgl.person),
      };

      if (isCustomGroup(group)) {
        const title = customGroupTitles[gKey] || group.title;
        groupData.title = title;
        groupData.type = 'CUSTOM';
        if (group.id !== null && (group.id as number) < 0) {
          groupData.allowedRoles = group.allowedRoles;
        }
      }

      newPermList.push(groupData);
    }

    // Also include any groups from allPermissionGroups not already in newPermList
    for (let j = 0; j < allPermissionGroups.length; j++) {
      const allGroup = allPermissionGroups[j];
      let foundInList = false;
      for (let k = 0; k < newPermList.length; k++) {
        if (newPermList[k].id === allGroup.id) {
          foundInList = true;
          break;
        }
      }
      if (!foundInList && deletedCustomGroupIds.indexOf(allGroup.id as number) === -1) {
        const allGroupKey = getGroupKey(allGroup.id);
        const allGroupPerms = permissionStore[allGroupKey] || {};
        const allPermissions: string[] = [];
        for (const p in allGroupPerms) {
          if (allGroupPerms.hasOwnProperty(p) && allGroupPerms[p] && isPermissionValidForGroup(p, allGroup)) {
            allPermissions.push(p);
          }
        }

        const allPersonGroupList = permissionGroupList.filter((pgl) => pgl.permissionGroup === allGroup.id);

        newPermList.push({
          id: allGroup.id,
          permissions: allPermissions,
          people: allPersonGroupList.map((pgl) => pgl.person),
        });
      }
    }

    api.updatePermissions(
      newPermList,
      (_result: any) => {
        App.showPopupNotification('Permissions Updated', 'success');
        setIsSaving(false);
        saveVisibleGroups(visibleGroupIds);
        preReloadGroupIdsRef.current = permissionGroups
          .map((g) => g.id)
          .filter((id) => id !== null && (id as number) > 0);
        setDeletedCustomGroupIds([]);
        setIsLoading(true);
        loadPermissions();
      },
      (error: any) => {
        setIsSaving(false);
        let errorMsg = 'Failed to save permissions.';
        if (error && error.message) {
          const friendlyMsg = (window as any).CUSTOM_GROUP_ERROR_MESSAGES[error.message];
          if (friendlyMsg) {
            errorMsg = friendlyMsg;
          } else {
            errorMsg = error.message;
          }
        }
        App.showPopupNotification(errorMsg, 'error');
      }
    );
  }, [
    permissionGroups,
    permissionStore,
    permissionGroupList,
    customGroupTitles,
    deletedCustomGroupIds,
    allPermissionGroups,
    visibleGroupIds,
    saveVisibleGroups,
    loadPermissions,
  ]);

  const discardChanges = useCallback(() => {
    // Restore permission store from cached initial state
    const newStore: { [groupId: string]: { [permission: string]: boolean } } = {};
    for (const groupId in initialPermissionStoreRef.current) {
      if (initialPermissionStoreRef.current.hasOwnProperty(groupId)) {
        newStore[groupId] = { ...initialPermissionStoreRef.current[groupId] };
      }
    }
    setPermissionStore(newStore);

    setPermissionGroupList(JSON.parse(JSON.stringify(initialPermissionGroupListRef.current)));
    setPermissionGroups(JSON.parse(JSON.stringify(initialPermissionGroupsRef.current)));

    const newTitles: { [groupId: string]: string } = {};
    for (const key in initialCustomGroupTitlesRef.current) {
      if (initialCustomGroupTitlesRef.current.hasOwnProperty(key)) {
        newTitles[key] = initialCustomGroupTitlesRef.current[key];
      }
    }
    setCustomGroupTitles(newTitles);

    setDeletedCustomGroupIds([]);
    nextTempIdRef.current = -1;

    const restoredVisibleIds = initialVisibleGroupIdsRef.current.slice();
    setVisibleGroupIds(restoredVisibleIds);
    saveVisibleGroups(restoredVisibleIds);

    App.showPopupNotification('Changes discarded', 'warning');
  }, [saveVisibleGroups]);

  // ── Render ──

  if (isLoading) {
    return (
      <div className={`permissions-screen ${theme}`}>
        <div className="permissions-header">
          <h1>Permissions</h1>
        </div>
        <div className="permissions-loading">
          <div className="loading-spinner"></div>
          <div>Loading permissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`permissions-screen ${theme}`}>
        {/* Header */}
        <div className="permissions-header">
          <h1>Permissions</h1>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              id="btnCreateCustomGroup"
              disabled={!canCreateCustomGroup}
              title={
                !canCreateCustomGroup
                  ? 'Maximum ' + MAX_CUSTOM_GROUPS_PER_COMPANY + ' custom groups allowed'
                  : 'Create a new custom permission group'
              }
              onClick={openCreateModal}
            >
              <i className="fa fa-plus"></i>
              {' Create Custom Group'}
            </button>
            {!canCreateCustomGroup && (
              <span className="header-limit-msg">
                <i className="fa fa-info-circle"></i> Max {MAX_CUSTOM_GROUPS_PER_COMPANY} custom groups reached
              </span>
            )}
            <button className="btn btn-default" id="btnDiscardChanges" onClick={discardChanges}>
              <i className="fa fa-undo"></i>
              {' Discard Changes'}
            </button>
            <button
              className="btn btn-success"
              id="btnSavePermissions"
              disabled={isSaving}
              onClick={savePermissions}
            >
              <i className={isSaving ? 'fa fa-spinner fa-spin' : 'fa fa-save'}></i>
              {isSaving ? ' Saving...' : ' Save'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="sub-navigation">
          <ul className="permissions-nav">
            <li className={activeTab === 'groups' ? 'active' : ''}>
              <a href="#" data-tab="groups" onClick={(e) => { e.preventDefault(); switchTab('groups'); }}>
                Group Permissions
              </a>
            </li>
            <li className={activeTab === 'users' ? 'active' : ''}>
              <a href="#" data-tab="users" onClick={(e) => { e.preventDefault(); switchTab('users'); }}>
                Individual Permissions
              </a>
            </li>
            {hasAuditLogPermission && (
              <li className={activeTab === 'auditlog' ? 'active' : ''}>
                <a href="#" data-tab="auditlog" onClick={(e) => { e.preventDefault(); switchTab('auditlog'); }}>
                  Audit Log
                </a>
              </li>
            )}
          </ul>
        </nav>

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <PermissionsGroupsTab
            searchTerm={searchTerm}
            permissionGroups={permissionGroups}
            visibleGroups={visibleGroups}
            filteredCategories={filteredCategories}
            permissionStore={permissionStore}
            editingGroupId={editingGroupId}
            groupTitleInputRef={groupTitleInputRef}
            maxGroupTitleLength={MAX_GROUP_TITLE_LENGTH}
            onSearchTermChange={setSearchTerm}
            onClearSearch={clearSearch}
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            isGroupVisible={isGroupVisible}
            onToggleGroupVisibility={toggleGroupVisibility}
            onShowAllGroups={showAllGroups}
            onHideAllGroups={hideAllGroups}
            getCategoryChildRows={getCategoryChildRows}
            isCategoryExpanded={isCategoryExpanded}
            onToggleCategory={toggleCategory}
            onToggleCategoryPermissions={toggleCategoryPermissions}
            onTogglePermission={togglePermission}
            isSearchMatch={isSearchMatch}
            getGroupDisplayTitle={getGroupDisplayTitle}
            onStartEditingGroupTitle={startEditingGroupTitle}
            onSaveGroupTitle={saveGroupTitle}
            onCancelEditingGroupTitle={cancelEditingGroupTitle}
            onOpenDeleteModal={openDeleteModal}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <PermissionsUsersTab
            permissionGroups={permissionGroups}
            sortedPeople={sortedPeople}
            selectedPeople={selectedPeople}
            isPersonSelected={isPersonSelected}
            isPersonInGroup={isPersonInGroup}
            onTogglePersonSelection={togglePersonSelection}
            onAddSelectedToGroup={addSelectedToGroup}
            onRemoveSelectedFromGroup={removeSelectedFromGroup}
          />
        )}
      </div>

      {/* Audit Log Tab (outside permissions-screen to avoid CSS reset conflicts) */}
      {hasAuditLogPermission && (
        <div className={theme} style={{ display: activeTab === 'auditlog' ? undefined : 'none' }}>
          <PermissionsAuditLog isVisible={activeTab === 'auditlog'} />
        </div>
      )}

      {/* Create Custom Group Modal */}
      <CreateCustomGroupModal
        open={showCreateModal}
        categories={categoriesForModal}
        people={sortedPeople}
        existingGroupTitles={existingGroupTitles}
        maxTitleLength={MAX_GROUP_TITLE_LENGTH}
        theme={theme}
        onSave={handleCreateModalSave}
        onCancel={closeCreateModal}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        open={showDeleteModal}
        title="Delete Custom Group"
        messageHtml={deleteModalMessageHtml}
        confirmText="Delete Group"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={confirmDeleteGroup}
        onCancel={closeDeleteModal}
      />
    </div>
  );
}

